import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { LogOut, Trash2, Plus, Newspaper, ArrowLeft, Loader2, Pencil, X } from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

const CATEGORIES = ["Plateforme", "Événement", "Territoire", "Partenariat"];

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await http.post("/auth/login", { email, password });
      onLogin(res.data);
      toast.success("Connexion réussie");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email ou mot de passe incorrect");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F4C4C] flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} data-testid="admin-login-form" className="bg-white p-8 lg:p-10 max-w-md w-full shadow-2xl">
        <h1 className="font-display text-2xl font-bold text-[#142828] mb-2">Espace administration</h1>
        <p className="text-sm text-[#516969] mb-8">Connectez-vous pour gérer les actualités VF ACTIONS.</p>
        <div className="mb-5">
          <label className="text-xs font-bold uppercase tracking-wider text-[#142828] block mb-2">Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-email-input"
            className="w-full bg-[#F6F4EC] border border-[#DAD5C8] px-4 py-3 text-sm" placeholder="admin@..." />
        </div>
        <div className="mb-6">
          <label className="text-xs font-bold uppercase tracking-wider text-[#142828] block mb-2">Mot de passe</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-password-input"
            className="w-full bg-[#F6F4EC] border border-[#DAD5C8] px-4 py-3 text-sm" placeholder="••••••••" />
        </div>
        <button type="submit" disabled={isSubmitting} data-testid="admin-login-btn"
          className="w-full bg-[#F5B800] text-[#082A2A] py-3.5 font-bold text-sm uppercase tracking-wider hover:bg-[#FFD54F] transition-colors disabled:opacity-50">
          {isSubmitting ? "Connexion..." : "Se connecter"}
        </button>
        <Link to="/" className="block text-center text-sm text-[#516969] hover:text-[#0F4C4C] mt-6 transition-colors" data-testid="admin-back-link">
          ← Retour au site
        </Link>
      </form>
    </div>
  );
};

const NewsManager = ({ user, onLogout }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const emptyForm = { title: "", content: "", category: "Territoire", date: new Date().toISOString().slice(0, 10) };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchNews = async () => {
    try {
      const res = await http.get("/news");
      setNews(res.data);
    } catch {
      toast.error("Impossible de charger les actualités");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNews(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (editingId) {
        await http.put(`/admin/news/${editingId}`, form);
        toast.success("Actualité mise à jour");
      } else {
        await http.post("/admin/news", form);
        toast.success("Actualité publiée");
      }
      setForm(emptyForm);
      setEditingId(null);
      fetchNews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({ title: item.title, content: item.content, category: item.category, date: item.date });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleDelete = async (id) => {
    try {
      await http.delete(`/admin/news/${id}`);
      toast.success("Actualité supprimée");
      setNews(news.filter((n) => n.id !== id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const handleLogout = async () => {
    try { await http.post("/auth/logout"); } catch { /* ignore */ }
    onLogout();
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#F6F4EC]" data-testid="admin-dashboard">
      <header className="bg-[#0F4C4C] text-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Newspaper className="w-5 h-5 text-[#F5B800]" />
            <h1 className="font-display font-bold">Gestion des actualités</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/70 hidden sm:block">{user.email}</span>
            <Link to="/" className="text-sm text-white/70 hover:text-[#F5B800] transition-colors inline-flex items-center gap-1" data-testid="admin-view-site">
              <ArrowLeft size={14} /> Voir le site
            </Link>
            <button onClick={handleLogout} data-testid="admin-logout-btn" className="text-sm text-white/70 hover:text-[#F5B800] transition-colors inline-flex items-center gap-1">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-5 gap-8">
        <form onSubmit={handleCreate} data-testid="admin-news-form" className="lg:col-span-2 bg-white border border-[#DAD5C8] p-6 h-fit">
          <h2 className="font-display text-lg font-bold text-[#142828] mb-5 flex items-center justify-between">
            <span className="flex items-center gap-2">
              {editingId ? <Pencil size={18} className="text-[#C49000]" /> : <Plus size={18} className="text-[#C49000]" />}
              {editingId ? "Modifier l'actualité" : "Nouvelle actualité"}
            </span>
            {editingId && (
              <button type="button" onClick={cancelEdit} data-testid="news-cancel-edit-btn" title="Annuler la modification"
                className="text-xs font-bold uppercase tracking-wider text-[#516969] hover:text-red-600 transition-colors inline-flex items-center gap-1">
                <X size={14} /> Annuler
              </button>
            )}
          </h2>
          <div className="mb-4">
            <label className="text-xs font-bold uppercase tracking-wider text-[#142828] block mb-2">Titre *</label>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required minLength={3} data-testid="news-title-input"
              className="w-full bg-[#F6F4EC] border border-[#DAD5C8] px-3 py-2.5 text-sm" placeholder="Titre de l'actualité" />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#142828] block mb-2">Catégorie *</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="news-category-select"
                className="w-full bg-[#F6F4EC] border border-[#DAD5C8] px-3 py-2.5 text-sm">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[#142828] block mb-2">Date *</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="news-date-input"
                className="w-full bg-[#F6F4EC] border border-[#DAD5C8] px-3 py-2.5 text-sm" />
            </div>
          </div>
          <div className="mb-5">
            <label className="text-xs font-bold uppercase tracking-wider text-[#142828] block mb-2">Contenu *</label>
            <textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required minLength={10} rows={5} data-testid="news-content-input"
              className="w-full bg-[#F6F4EC] border border-[#DAD5C8] px-3 py-2.5 text-sm resize-none" placeholder="Quelques lignes sur l'avancée, l'événement..." />
          </div>
          <button type="submit" disabled={isSubmitting} data-testid="news-submit-btn"
            className="w-full bg-[#0F4C4C] text-white py-3 font-bold text-sm uppercase tracking-wider hover:bg-[#1A6B6B] transition-colors disabled:opacity-50">
            {isSubmitting ? "Enregistrement..." : editingId ? "Enregistrer les modifications" : "Publier"}
          </button>
        </form>

        <div className="lg:col-span-3">
          <h2 className="font-display text-lg font-bold text-[#142828] mb-5">Actualités publiées ({news.length})</h2>
          {loading ? (
            <div className="flex items-center gap-2 text-[#516969] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Chargement...</div>
          ) : news.length === 0 ? (
            <p className="text-sm text-[#516969]" data-testid="admin-news-empty">Aucune actualité publiée.</p>
          ) : (
            <div className="space-y-4">
              {news.map((item) => (
                <article key={item.id} data-testid={`news-item-${item.id}`} className={`bg-white border p-5 flex items-start justify-between gap-4 ${editingId === item.id ? "border-[#F5B800] ring-2 ring-[#F5B800]/30" : "border-[#DAD5C8]"}`}>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="bg-[#F5B800]/20 text-[#C49000] text-xs font-bold uppercase tracking-wider px-2 py-0.5">{item.category}</span>
                      <span className="text-xs text-[#516969]">{formatDate(item.date)}</span>
                    </div>
                    <h3 className="font-display font-bold text-[#142828] mb-1">{item.title}</h3>
                    <p className="text-sm text-[#516969] leading-relaxed">{item.content}</p>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button onClick={() => startEdit(item)} data-testid={`news-edit-${item.id}`} title="Modifier"
                      className="text-[#516969] hover:text-[#0F4C4C] transition-colors p-2">
                      <Pencil size={17} />
                    </button>
                    <button onClick={() => handleDelete(item.id)} data-testid={`news-delete-${item.id}`} title="Supprimer"
                      className="text-[#516969] hover:text-red-600 transition-colors p-2">
                      <Trash2 size={17} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    http.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#F6F4EC] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#0F4C4C]" />
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" richColors />
      {user ? <NewsManager user={user} onLogout={() => setUser(null)} /> : <LoginForm onLogin={setUser} />}
    </>
  );
}
